/**
 * Centralized query keys for TanStack Query
 * Use these for consistency and easier invalidation
 *
 * When a key has optional params (e.g. cashWithdrawals with date), invalidate from
 * contexts that may lack them using the *All prefix helper so all param-specific
 * queries are invalidated.
 */

export const queryKeys = {
  // Operational data - changes frequently during service
  tabs: (orgId: string) => ['tabs', orgId] as const,
  kitchenTickets: (orgId: string) => ['kitchenTickets', orgId] as const,
  externalOrders: (orgId: string) => ['externalOrders', orgId] as const,
  cashBalances: (orgId: string) => ['cashBalances', orgId] as const,
  shiftClosures: (orgId: string, date?: string) => ['shiftClosures', orgId, date] as const,
  cashWithdrawals: (orgId: string, date?: string) =>
    ['cashWithdrawals', orgId, 'date', date] as const,
  cashWithdrawalsRange: (orgId: string, fromKey?: string, toKey?: string) =>
    ['cashWithdrawals', orgId, 'range', fromKey, toKey] as const,
  /** Prefix for invalidating all cash withdrawal queries for an org (use when date may be unknown) */
  cashWithdrawalsAll: (orgId: string) => ['cashWithdrawals', orgId] as const,
  sales: (orgId: string, fromKey?: string, toKey?: string) => ['sales', orgId, fromKey, toKey] as const,
  /** Prefix for invalidating all sales queries for an org (use when date range may be unknown) */
  salesAll: (orgId: string) => ['sales', orgId] as const,
  reservations: (orgId: string, fromKey?: string, toKey?: string) =>
    ['reservations', orgId, fromKey, toKey] as const,
  /** Prefix for invalidating all reservation queries for an org (use when date range may be unknown) */
  reservationsAll: (orgId: string) => ['reservations', orgId] as const,


  // Product catalog - moderate change frequency
  products: (orgId: string) => ['products', orgId] as const,
  productsForSales: (orgId: string) => ['productsForSales', orgId] as const,
  productModifiers: (orgId: string) => ['productModifiers', orgId] as const,
  productModifierCategories: (orgId: string) => ['productModifierCategories', orgId] as const,
  /** Product bundle / promotion definitions for an org */
  promotions: (orgId: string) => ['promotions', orgId] as const,
  ingredients: (orgId: string) => ['ingredients', orgId] as const,
  productIngredientsCatalog: (orgId: string) => ['productIngredientsCatalog', orgId] as const,
  productCategories: (orgId: string) => ['productCategories', orgId] as const,
  productCategoriesForSales: (orgId: string) => ['productCategoriesForSales', orgId] as const,
  invoiceStatus: (invoiceId: string) => ['invoiceStatus', invoiceId] as const,
  /** Company tax regime; source of truth is the Hacienda microservice. */
  companyRegime: (orgId: string) => ['companyRegime', orgId] as const,
  /** Full CR territorial tree — Hacienda `GET /locations/all`. */
  locationsAll: (orgId: string) => ['locations', orgId, 'all'] as const,
  sinpePhotoUrl: (storagePath: string) => ['sinpePhotoUrl', storagePath] as const,

  // Reference data - changes infrequently
  providers: (orgId: string) => ['providers', orgId] as const,
  paymentMethods: (orgId: string) => ['paymentMethods', orgId] as const,
  tablesLayout: (orgId: string) => ['organization-tables', orgId] as const,
  /** Prefix: invalidates settings + fast-switch (and any future org-members queries). */
  organizationMembersAll: (orgId: string) => ['organization-members', orgId] as const,
  organizationMembersSettings: (orgId: string) =>
    ['organization-members', orgId, 'settings'] as const,
  organizationMembersFastSwitch: (orgId: string) =>
    ['organization-members', orgId, 'fast-switch'] as const,
  messages: (orgId: string) => ['messages', orgId] as const,
  messagesActive: (orgId: string) => ['messages', orgId, 'active'] as const,
  headerMessages: (orgId: string, slug: string) => ['headerMessages', orgId, slug] as const,
  acceptedOrdersHistory: (orgId: string, monthStr?: string) => ['acceptedOrdersHistory', orgId, monthStr] as const,
  acceptedOrdersHistoryAll: (orgId: string) => ['acceptedOrdersHistory', orgId] as const,
  orderExists: (orderId: string, orgId: string) => ['orderExists', orderId, orgId] as const,
  tabItemDeletionHistory: (orgId: string, fromKey?: string, toKey?: string) =>
    ['tabItemDeletionHistory', orgId, fromKey, toKey] as const,
  tabItemDeletionHistoryAll: (orgId: string) => ['tabItemDeletionHistory', orgId] as const,
  tabDeletionHistory: (orgId: string, fromKey?: string, toKey?: string) =>
    ['tabDeletionHistory', orgId, fromKey, toKey] as const,
  tabDeletionHistoryAll: (orgId: string) => ['tabDeletionHistory', orgId] as const,
  discountApplicationHistory: (orgId: string, fromKey?: string, toKey?: string) =>
    ['discountApplicationHistory', orgId, fromKey, toKey] as const,
  discountApplicationHistoryAll: (orgId: string) => ['discountApplicationHistory', orgId] as const,
  /** Local provider orders (invoices retrieved from email) pending import in Tabstr. */
  providerOrders: (orgId: string) => ['providerOrders', orgId] as const,
  subscriptionCatalogPlans: (organizationSlug: string) => ['subscriptionCatalogPlans', organizationSlug] as const,
  subscriptionBillingHistory: (organizationSlug: string) =>['subscriptionBillingHistory', organizationSlug] as const,
  subscriptionCurrent: (organizationSlug: string, paymentSearchValue: string | null) =>['subscriptionCurrent', organizationSlug, paymentSearchValue] as const,
  /** Registry edge: is_suspended + subscription status for the org. */
  organizationStatus: (organizationSlug: string) =>
    ['organizationStatus', organizationSlug] as const,

  // Simple users
  simpleUsers: (orgId: string, scope: 'active' | 'all') => ['simpleUsers', orgId, scope] as const,
  /** Prefix for invalidating all simple user queries for an org */
  simpleUsersRoot: (orgId: string) => ['simpleUsers', orgId] as const,

  // Client sales reports
  clientSalesReport: (orgId: string, fromKey?: string, toKey?: string) =>
    ['clientSalesReport', orgId, fromKey, toKey] as const,
  /** Prefix for invalidating all client sales report queries for an org */
  clientSalesReportAll: (orgId: string) => ['clientSalesReport', orgId] as const,

  // Organization & auth - changes rarely
  /** One row per org slug; membership is derived in the query from current auth (member vs simple). */
  organization: (slug: string) => ['organization', slug] as const,
  featureFlags: (slug: string) => ['featureFlags', slug] as const,
  profile: (userId: string) => ['profile', userId] as const,
  profileByEmail: (orgId: string, email: string) => ['profile-by-email', orgId, email] as const,

   // External reference - changes daily
   dollarValue: () => ['dollarValue'] as const,
} as const;
